import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { get, map, toString, snakeCase } from 'lodash';
import { Notification, Box, TextField, TextArea, Label, Spinner, FlexCol, FlexRow } from 'sarsaparilla';
import { useDispatch } from 'react-redux';
import { OrgOptionProps } from '../../props/organization';
import { SelectInput } from "../../containers/StewardAssetEditPage/ValidationWrapper";
import { getRef, addChange } from '../../actions/changes';

import SuggestSearch from './SuggestSearch';
import CheckBoxInput from "./CheckBoxInput";

import {
    NAME_PATH,
    ID_PATH,
    EMAIL_PATH,
    FEE_DESCRIPTION_PATH,
    LEGACY_ID_PATH,
    ORG_ID_PATH,
    ORG_REF_ID_PATH,
    LATITUDE_PATH,
    LONGITUDE_PATH,
    PHONE_PATH,
    DESCRIPTION_PATH,
    DIRECTIONS_PATH,
    KEYWORDS_PATH,
    STAY_LIMIT_PATH
} from '../../constants/paths';

const propTypes = {
    value: PropTypes.object,
    assetDetails: PropTypes.shape({
        name: PropTypes.string,
        singular: PropTypes.string,
        plural: PropTypes.string
    }),
    orgOptions: PropTypes.arrayOf(OrgOptionProps),
    isSuccessful: PropTypes.bool,
    isLoading: PropTypes.bool
};

const defaultProps = {};

const AssetTab = ({
    value,
    assetDetails,
    orgOptions,
    isSuccessful,
    isLoading
}) => {
    const dispatch = useDispatch();
    const [pendingValue, updateValue] = useState(value);
    const [ref, updateRef] = useState(null);

    useEffect(() => {
        updateValue(value);
        const newRef = dispatch(getRef(
            assetDetails.plural,
            { id: value.id },
            -1,
            [NAME_PATH, ORG_ID_PATH, DESCRIPTION_PATH, DIRECTIONS_PATH].reduce((m, f) => Object.assign(m, { [f]: value[f] }), {})
        ));
        updateRef(newRef);
    }, [value]);

    const selectInputs =[
        {
            label: 'Organization',
            key: 'orgId',
            field: ORG_ID_PATH,
            required: true,
            fullWidth: true,
            size: Math.max(1, (orgOptions || []).length),
            options: orgOptions || []
        }
    ];

    const textInputs = [
        {
            label: `${assetDetails.name} Name`,
            required: true,
            field: NAME_PATH
        },
        {
            label: `${assetDetails.name} ID`,
            readOnly: true,
            field: ID_PATH
        },
        {
            label: 'Email',
            field: EMAIL_PATH
        },
        {
            label: 'Fee Description',
            field: FEE_DESCRIPTION_PATH
        },
        {
            label: 'Legacy ID',
            field: LEGACY_ID_PATH
        },
        {
            label: 'Legacy ID Organization',
            field: ORG_REF_ID_PATH
        },
        {
            label: 'Latitude',
            field: LATITUDE_PATH
        },
        {
            label: 'Longitude',
            field: LONGITUDE_PATH
        },
        {
            label: 'Phone',
            field: PHONE_PATH
        }
    ];

    const textBoxInputs = [
        {
            label: 'Description',
            field: DESCRIPTION_PATH,
            required: true,
            fullWidth: true,
            maxChar: 4000
        },
        {
            label: 'Directions',
            field: DIRECTIONS_PATH,
            required: true,
            fullWidth: true,
            maxChar: 4000
        },
        {
            label: 'Keywords',
            field: KEYWORDS_PATH,
            maxChar: 400
        },
        {
            label: 'Stay Limit',
            field: STAY_LIMIT_PATH,
            maxChar: 400
        }
    ]

    const handleChange = (event, path) => {
        const { value: newValue } = event.target;
        const change = { [path]: newValue };
        const newValues = { ...pendingValue, ...change };
        updateValue(newValues);
        dispatch(addChange(ref, change));
    }

    const query = { limit: 10, assetTypes: ['recarea'], orgIds: orgOptions.map((org) => org.value) };
    

    if (isLoading) {
        return <Spinner isCentered={false} size="xl" />
    }

    return (
        <Box
            padding="md"
            border="gray"
            background="white"
            marginY="md">
            { isSuccessful && (
                <Notification type="success">
                    Submission successful
                </Notification>
            )}

            <FlexRow>
                { map(textInputs, (textInput) => (
                    <FlexCol md={6} key={snakeCase(textInput.field)}>
                        <TextField
                            id={textInput.field}
                            label={textInput.label}
                            className="mb-1"
                            value={toString(get(pendingValue, textInput.field, ''))}
                            onChange={(e) => handleChange(e, textInput.field)}
                            isDisabled={textInput.readOnly}
                            isRequired={textInput.required} />
                    </FlexCol>
                    
                ))}
            </FlexRow>

            { assetDetails.name === 'Facility' && (
                <div>
                    <Label htmlFor="parentRecArea">
                        Parent Rec Area
                    </Label>
                    <SuggestSearch
                        id="parentRecArea"
                        field="recareas[0]"
                        idField="recAreaId"
                        value={get(pendingValue, 'recareas[0]')}
                        query={query}
                        placeholder="search Rec Areas by name..."
                        onChange={handleChange} />
                </div>
            )}

            <CheckBoxInput
                value={get(pendingValue, 'enabled')}
                label="Enabled"
                field="enabled"
                onChange={handleChange} />

            { map(selectInputs, (selectInputProps) => (
                <SelectInput
                    key={snakeCase(selectInputProps.field)}
                    {...selectInputProps}
                    changeRef={ref}
                    onChange={(e) => handleChange(e, selectInputProps.field)}
                    value={get(pendingValue, selectInputProps.field)} />
            ))}

            { map(textBoxInputs, (textBoxInput) => (
                <TextArea
                    key={snakeCase(textBoxInput.field)}
                    id={textBoxInput.field}
                    label={textBoxInput.label}
                    onChange={(e) => handleChange(e, textBoxInput.field)}
                    value={get(pendingValue, textBoxInput.field, '')}
                    isRequired={textBoxInput.required}
                    rows={5} />
            ))}
        </Box>
    );
}
AssetTab.propTypes = propTypes;
AssetTab.defaultProps = defaultProps;

export default AssetTab;

